import { useState } from "react";
import { useNavigate, Navigate, useLocation } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import QRCode from "react-qr-code";
import { ShieldCheck, ArrowLeft } from "lucide-react";

// Obscurity-only gate. Real security = password + 2FA. This just hides the
// login form from casual URL guessers. Override by setting REACT_APP_ADMIN_KEY
// in the frontend env.
const ADMIN_ACCESS_KEY = process.env.REACT_APP_ADMIN_KEY || "lakeunice2026";

const AdminLogin = () => {
  const { login, mfaSetupBegin, mfaSetupConfirm, mfaVerify,
          smsSetupStart, smsSetupConfirm, smsLoginResend, smsLoginVerify,
          isAuthed, loading } = useAuth();
  const nav = useNavigate();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const hasValidKey = params.get("key") === ADMIN_ACCESS_KEY;

  const [step, setStep] = useState("password"); // password | mfa | setup | smsSetup | smsVerify
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  // MFA session state
  const [mfaToken, setMfaToken] = useState("");
  const [setupData, setSetupData] = useState(null); // { secret, otpauth_uri }

  // SMS 2FA state
  const [smsToken, setSmsToken] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneMasked, setPhoneMasked] = useState("");
  const [smsCodeSent, setSmsCodeSent] = useState(false);

  if (!loading && isAuthed) return <Navigate to="/admin" replace />;

  // Obscurity gate: without ?key=..., show a generic 404 so casual visitors
  // who guess /admin/login see nothing useful.
  if (!hasValidKey) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4" data-testid="admin-login-404">
        <div className="text-center">
          <h1 className="text-5xl font-serif text-primary mb-3">404</h1>
          <p className="text-muted-foreground text-base mb-6">The page you&apos;re looking for doesn&apos;t exist.</p>
          <Button variant="outline" onClick={() => nav("/")} data-testid="admin-login-404-home">
            Return to homepage
          </Button>
        </div>
      </div>
    );
  }

  const startOver = () => {
    setStep("password");
    setMfaToken("");
    setSetupData(null);
    setCode("");
    setSmsToken("");
    setPhone("");
    setPhoneMasked("");
    setSmsCodeSent(false);
  };

  const onPassword = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const result = await login(email.trim().toLowerCase(), pw);
      if (result.kind === "ok") {
        toast.success("Welcome back.");
        nav("/admin");
        return;
      }
      if (result.kind === "sms") {
        setSmsToken(result.smsToken);
        setPhoneMasked(result.phoneMasked || "");
        setStep("smsVerify");
        return;
      }
      if (result.kind === "smsSetup") {
        setSmsToken(result.smsToken);
        setStep("smsSetup");
        return;
      }
      setMfaToken(result.mfaToken);
      if (result.kind === "mfa") {
        setStep("mfa");
      } else if (result.kind === "setup") {
        const data = await mfaSetupBegin(result.mfaToken);
        setSetupData(data);
        setStep("setup");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid credentials");
    } finally {
      setBusy(false);
    }
  };

  const onSmsSetupSend = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await smsSetupStart(smsToken, phone);
      setPhoneMasked(res.phone_masked || phone);
      setSmsCodeSent(true);
      setCode("");
      toast.success(`Code sent to ${res.phone_masked || phone}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send code");
    } finally {
      setBusy(false);
    }
  };

  const onSmsSetupConfirm = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await smsSetupConfirm(smsToken, phone, code);
      toast.success("Phone verified. You're signed in.");
      nav("/admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid or expired code");
    } finally {
      setBusy(false);
    }
  };

  const onSmsVerify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await smsLoginVerify(smsToken, code);
      toast.success("Welcome back.");
      nav("/admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid or expired code");
    } finally {
      setBusy(false);
    }
  };

  const onSmsResend = async () => {
    setBusy(true);
    try {
      const res = await smsLoginResend(smsToken);
      setPhoneMasked(res.phone_masked || phoneMasked);
      setCode("");
      toast.success(`New code sent to ${res.phone_masked || phoneMasked}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not resend code");
    } finally {
      setBusy(false);
    }
  };

  const onMfaVerify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await mfaVerify(mfaToken, code);
      toast.success("Welcome back.");
      nav("/admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid code");
    } finally {
      setBusy(false);
    }
  };

  const onSetupConfirm = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await mfaSetupConfirm(mfaToken, code);
      toast.success("2FA enabled. You're signed in.");
      nav("/admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid code");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 px-4 py-12" data-testid="admin-login-page">
      <Card className="w-full max-w-md border-border">
        <CardContent className="p-8">
          <div className="text-xs uppercase tracking-[0.25em] text-secondary mb-2">Township Admin</div>

          {step === "password" && (
            <>
              <h1 className="font-heading text-3xl font-semibold tracking-tight mb-1">Sign in</h1>
              <p className="text-sm text-muted-foreground mb-6">Manage the Lake Eunice Township website.</p>
              <form onSubmit={onPassword} className="space-y-4" data-testid="login-step-password">
                <div>
                  <Label htmlFor="email">Email or username</Label>
                  <Input
                    id="email"
                    type="text"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="username"
                    className="mt-1.5"
                    data-testid="admin-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="pw">Password</Label>
                  <Input
                    id="pw"
                    type="password"
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    required
                    className="mt-1.5"
                    data-testid="admin-password-input"
                  />
                </div>
                <Button type="submit" disabled={busy} className="w-full bg-primary hover:bg-primary/90" data-testid="admin-login-submit">
                  {busy ? "Signing in..." : "Continue"}
                </Button>
              </form>
            </>
          )}

          {step === "mfa" && (
            <>
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck className="text-accent" size={18} />
                <h1 className="font-heading text-2xl font-semibold tracking-tight">Two-factor code</h1>
              </div>
              <p className="text-sm text-muted-foreground mb-6">
                Open your authenticator app (Google Authenticator, Authy, 1Password, etc.) and enter the 6-digit code for Lake Eunice Township.
              </p>
              <form onSubmit={onMfaVerify} className="space-y-4" data-testid="login-step-mfa">
                <div>
                  <Label htmlFor="mfa-code">6-digit code</Label>
                  <Input
                    id="mfa-code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="[0-9]*"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    required
                    placeholder="000000"
                    className="mt-1.5 text-center tracking-[0.4em] font-mono text-lg"
                    data-testid="admin-mfa-code-input"
                  />
                </div>
                <Button type="submit" disabled={busy || code.length !== 6} className="w-full bg-primary hover:bg-primary/90" data-testid="admin-mfa-verify-btn">
                  {busy ? "Verifying..." : "Verify"}
                </Button>
                <button type="button" onClick={startOver} className="w-full text-xs text-muted-foreground hover:text-foreground inline-flex items-center justify-center gap-1.5 mt-2">
                  <ArrowLeft size={12} /> Use a different account
                </button>
              </form>
            </>
          )}

          {step === "setup" && setupData && (
            <>
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck className="text-accent" size={18} />
                <h1 className="font-heading text-2xl font-semibold tracking-tight">Set up 2FA</h1>
              </div>
              <p className="text-sm text-muted-foreground mb-5">
                Two-factor authentication is required for this site. Scan the QR code with your authenticator app, then enter the 6-digit code to finish signing in.
              </p>

              <div className="bg-white border border-border rounded-lg p-4 flex flex-col items-center mb-5" data-testid="admin-mfa-qr">
                <QRCode value={setupData.otpauth_uri} size={176} />
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-3">Or enter this secret manually</div>
                <code className="text-xs font-mono mt-1 select-all bg-muted/40 px-2 py-1 rounded" data-testid="admin-mfa-secret">{setupData.secret}</code>
              </div>

              <form onSubmit={onSetupConfirm} className="space-y-4" data-testid="login-step-setup">
                <div>
                  <Label htmlFor="setup-code">6-digit code from your app</Label>
                  <Input
                    id="setup-code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="[0-9]*"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    required
                    placeholder="000000"
                    className="mt-1.5 text-center tracking-[0.4em] font-mono text-lg"
                    data-testid="admin-mfa-setup-code-input"
                  />
                </div>
                <Button type="submit" disabled={busy || code.length !== 6} className="w-full bg-primary hover:bg-primary/90" data-testid="admin-mfa-setup-confirm-btn">
                  {busy ? "Enabling..." : "Enable 2FA & sign in"}
                </Button>
                <p className="text-xs text-muted-foreground text-center">
                  Tip: keep a backup. Save the secret above somewhere safe (password manager) in case you lose access to your phone.
                </p>
              </form>
            </>
          )}

          {step === "smsSetup" && (
            <>
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck className="text-accent" size={18} />
                <h1 className="font-heading text-2xl font-semibold tracking-tight">Set up SMS 2FA</h1>
              </div>
              <p className="text-sm text-muted-foreground mb-5">
                {!smsCodeSent
                  ? "For extra security, we'll text a 6-digit code to your phone whenever you sign in. Enter the mobile number that should receive these codes."
                  : `We texted a 6-digit code to ${phoneMasked}. Enter it below to verify this phone.`}
              </p>

              {!smsCodeSent && (
                <form onSubmit={onSmsSetupSend} className="space-y-4" data-testid="login-step-sms-setup">
                  <div>
                    <Label htmlFor="phone">Mobile phone number</Label>
                    <Input
                      id="phone"
                      type="tel"
                      autoComplete="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      required
                      placeholder="+1 (218) 555-1234"
                      className="mt-1.5"
                      data-testid="admin-sms-phone-input"
                    />
                    <p className="text-[11px] text-muted-foreground mt-1.5">Standard SMS rates may apply. We only use this number for login codes.</p>
                  </div>
                  <Button type="submit" disabled={busy || phone.length < 7} className="w-full bg-primary hover:bg-primary/90" data-testid="admin-sms-send-code-btn">
                    {busy ? "Sending..." : "Send code"}
                  </Button>
                  <button type="button" onClick={startOver} className="w-full text-xs text-muted-foreground hover:text-foreground inline-flex items-center justify-center gap-1.5 mt-2">
                    <ArrowLeft size={12} /> Use a different account
                  </button>
                </form>
              )}

              {smsCodeSent && (
                <form onSubmit={onSmsSetupConfirm} className="space-y-4" data-testid="login-step-sms-setup-verify">
                  <div>
                    <Label htmlFor="sms-setup-code">6-digit code</Label>
                    <Input
                      id="sms-setup-code"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      pattern="[0-9]*"
                      maxLength={6}
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                      required
                      placeholder="000000"
                      className="mt-1.5 text-center tracking-[0.4em] font-mono text-lg"
                      data-testid="admin-sms-setup-code-input"
                    />
                  </div>
                  <Button type="submit" disabled={busy || code.length !== 6} className="w-full bg-primary hover:bg-primary/90" data-testid="admin-sms-setup-confirm-btn">
                    {busy ? "Verifying..." : "Verify & sign in"}
                  </Button>
                  <button type="button" onClick={() => { setSmsCodeSent(false); setCode(""); }} className="w-full text-xs text-muted-foreground hover:text-foreground inline-flex items-center justify-center gap-1.5 mt-2">
                    <ArrowLeft size={12} /> Use a different phone number
                  </button>
                </form>
              )}
            </>
          )}

          {step === "smsVerify" && (
            <>
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck className="text-accent" size={18} />
                <h1 className="font-heading text-2xl font-semibold tracking-tight">Enter verification code</h1>
              </div>
              <p className="text-sm text-muted-foreground mb-6">
                We texted a 6-digit code to {phoneMasked || "your phone"}. Enter it below to finish signing in.
              </p>
              <form onSubmit={onSmsVerify} className="space-y-4" data-testid="login-step-sms-verify">
                <div>
                  <Label htmlFor="sms-code">6-digit code</Label>
                  <Input
                    id="sms-code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="[0-9]*"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    required
                    placeholder="000000"
                    className="mt-1.5 text-center tracking-[0.4em] font-mono text-lg"
                    data-testid="admin-sms-code-input"
                  />
                </div>
                <Button type="submit" disabled={busy || code.length !== 6} className="w-full bg-primary hover:bg-primary/90" data-testid="admin-sms-verify-btn">
                  {busy ? "Verifying..." : "Verify"}
                </Button>
                <div className="flex items-center justify-between text-xs text-muted-foreground pt-1">
                  <button type="button" onClick={startOver} className="hover:text-foreground inline-flex items-center gap-1.5">
                    <ArrowLeft size={12} /> Use different account
                  </button>
                  <button type="button" onClick={onSmsResend} disabled={busy} className="hover:text-foreground disabled:opacity-50" data-testid="admin-sms-resend-btn">
                    Resend code
                  </button>
                </div>
              </form>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLogin;
